import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../../config/supabase';
import './BBMappingPage.css';

/**
 * BBMappingPage - Admin tool for mapping master_items to Blue Book chapters.
 * Temporary page for Phase 4 of the BB integration.
 * Access via /admin/bb-mapping (no sidebar link needed).
 */
const BBMappingPage = () => {
  // Data state
  const [masterItems, setMasterItems] = useState([]);
  const [bbChapters, setBbChapters] = useState([]);
  const [suggestions, setSuggestions] = useState({});
  const [itemTypes, setItemTypes] = useState({});
  const [loading, setLoading] = useState(true);

  // UI state
  const [activeTab, setActiveTab] = useState('items'); // 'items' | 'bundles'
  const [filter, setFilter] = useState('all'); // all, unmapped, pending, approved, low-confidence
  const [searchTerm, setSearchTerm] = useState('');
  const [apiKey] = useState(process.env.REACT_APP_CLAUDE_API_KEY || '');
  const [aiRunning, setAiRunning] = useState(false);
  const [aiProgress, setAiProgress] = useState({ current: 0, total: 0 });
  const [mapByTypeId, setMapByTypeId] = useState(''); // selected type for bulk mapping
  const [bundleItems, setBundleItems] = useState([]);

  // ─── Data fetching ───────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all data in parallel
      const [itemsRes, chaptersRes, suggestionsRes, typesRes, bundlesRes] = await Promise.all([
        supabase
          .from('master_items')
          .select('id, item_name, item_type_id, bb_chapter_id, boq_description, is_bundle')
          .eq('is_bundle', false)
          .order('item_name'),
        supabase
          .from('reference_bb_chapters')
          .select('id, code, name_he, depth, parent_id')
          .in('depth', [0, 1])
          .order('code'),
        supabase.from('bb_mapping_suggestions').select('*'),
        supabase.from('reference_item_types').select('id, name'),
        supabase
          .from('master_items')
          .select('id, item_name, bb_chapter_id, bundle_type')
          .eq('is_bundle', true)
          .order('item_name'),
      ]);

      if (itemsRes.error) throw itemsRes.error;
      if (chaptersRes.error) throw chaptersRes.error;
      if (suggestionsRes.error) throw suggestionsRes.error;
      if (typesRes.error) throw typesRes.error;
      if (bundlesRes.error) throw bundlesRes.error;

      setMasterItems(itemsRes.data || []);
      setBundleItems(bundlesRes.data || []);
      setBbChapters(chaptersRes.data || []);

      // Index suggestions by master_item_id for quick lookup
      const sugMap = {};
      (suggestionsRes.data || []).forEach(s => {
        sugMap[s.master_item_id] = s;
      });
      setSuggestions(sugMap);

      // Index item types by id
      const typeMap = {};
      (typesRes.data || []).forEach(t => {
        typeMap[t.id] = t.name;
      });
      setItemTypes(typeMap);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─── Computed values ─────────────────────────────────────────────

  // Helper: check if an item is "mapped" (approved or manual in suggestions)
  const isMapped = useCallback(
    itemId => {
      const s = suggestions[itemId];
      return s && (s.status === 'approved' || s.status === 'manual');
    },
    [suggestions],
  );

  // Helper: get the mapped chapter ID from suggestions
  const getMappedChapterId = useCallback(
    itemId => {
      const s = suggestions[itemId];
      return s && (s.status === 'approved' || s.status === 'manual')
        ? s.suggested_bb_chapter_id
        : '';
    },
    [suggestions],
  );

  const stats = useMemo(() => {
    const total = masterItems.length;
    const mapped = masterItems.filter(i => isMapped(i.id)).length;
    const pending = Object.values(suggestions).filter(s => s.status === 'pending').length;
    const approved = Object.values(suggestions).filter(
      s => s.status === 'approved' || s.status === 'manual',
    ).length;
    return { total, mapped, unmapped: total - mapped, pending, approved };
  }, [masterItems, suggestions, isMapped]);

  // Filter and search items
  const filteredItems = useMemo(() => {
    let items = masterItems;

    // Apply filter
    switch (filter) {
      case 'unmapped':
        items = items.filter(i => !isMapped(i.id));
        break;
      case 'pending':
        items = items.filter(i => suggestions[i.id]?.status === 'pending');
        break;
      case 'approved':
        items = items.filter(i => isMapped(i.id));
        break;
      case 'low-confidence':
        items = items.filter(i => {
          const s = suggestions[i.id];
          return s && s.confidence < 0.8;
        });
        break;
      default:
        break;
    }

    // Apply search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      items = items.filter(
        i =>
          i.item_name.toLowerCase().includes(term) ||
          (itemTypes[i.item_type_id] || '').toLowerCase().includes(term),
      );
    }

    return items;
  }, [masterItems, filter, searchTerm, suggestions, itemTypes]);

  // ─── Actions ─────────────────────────────────────────────────────

  // Approve an AI suggestion → only update suggestion status (no master_items write yet)
  const handleApprove = async itemId => {
    const suggestion = suggestions[itemId];
    if (!suggestion?.suggested_bb_chapter_id) return;

    try {
      // Mark suggestion as approved
      const { error: sugError } = await supabase
        .from('bb_mapping_suggestions')
        .update({ status: 'approved' })
        .eq('master_item_id', itemId);
      if (sugError) throw sugError;

      // Update local state
      setSuggestions(prev => ({
        ...prev,
        [itemId]: { ...prev[itemId], status: 'approved' },
      }));
    } catch (err) {
      console.error('Error approving suggestion:', err);
      alert('שגיאה באישור: ' + err.message);
    }
  };

  // Manual override → only write to suggestions table (no master_items write yet)
  const handleManualMap = async (itemId, chapterId) => {
    if (!chapterId) return;

    try {
      // Upsert suggestion as 'manual'
      const { error: sugError } = await supabase.from('bb_mapping_suggestions').upsert(
        {
          master_item_id: itemId,
          suggested_bb_chapter_id: chapterId,
          status: 'manual',
          confidence: 1.0,
          ai_reasoning: 'Manual mapping by admin',
        },
        { onConflict: 'master_item_id' },
      );
      if (sugError) throw sugError;

      // Update local state
      setSuggestions(prev => ({
        ...prev,
        [itemId]: {
          ...prev[itemId],
          master_item_id: itemId,
          suggested_bb_chapter_id: chapterId,
          status: 'manual',
          confidence: 1.0,
          ai_reasoning: 'Manual mapping by admin',
        },
      }));
    } catch (err) {
      console.error('Error setting manual mapping:', err);
      alert('שגיאה בשמירה: ' + err.message);
    }
  };

  // Clear a mapping (delete suggestion, reset to unmapped)
  const handleClearMapping = async itemId => {
    try {
      const { error } = await supabase
        .from('bb_mapping_suggestions')
        .delete()
        .eq('master_item_id', itemId);
      if (error) throw error;

      setSuggestions(prev => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
    } catch (err) {
      console.error('Error clearing mapping:', err);
      alert('שגיאה במחיקה: ' + err.message);
    }
  };

  // Reject an AI suggestion
  const handleReject = async itemId => {
    const suggestion = suggestions[itemId];
    if (!suggestion) return;

    try {
      const { error } = await supabase
        .from('bb_mapping_suggestions')
        .update({ status: 'rejected' })
        .eq('master_item_id', itemId);
      if (error) throw error;

      setSuggestions(prev => ({
        ...prev,
        [itemId]: { ...prev[itemId], status: 'rejected' },
      }));
    } catch (err) {
      console.error('Error rejecting suggestion:', err);
      alert('שגיאה בדחייה: ' + err.message);
    }
  };

  // Reject all pending suggestions for a specific item type
  const handleRejectByType = async typeId => {
    const itemsOfType = masterItems.filter(i => {
      const s = suggestions[i.id];
      return i.item_type_id === typeId && s && s.status === 'pending';
    });

    if (itemsOfType.length === 0) {
      alert('אין הצעות ממתינות לסוג זה');
      return;
    }

    const typeName = itemTypes[typeId] || typeId;
    if (!window.confirm(`לדחות ${itemsOfType.length} הצעות מסוג "${typeName}"?`)) return;

    for (const item of itemsOfType) {
      await handleReject(item.id);
    }
  };

  // Batch approve all high-confidence suggestions (skips rejected)
  const handleBatchApprove = async () => {
    const highConfidence = masterItems.filter(i => {
      const s = suggestions[i.id];
      return s && s.status === 'pending' && s.confidence >= 0.8 && !isMapped(i.id);
    });

    if (highConfidence.length === 0) {
      alert('אין הצעות ברמת ביטחון גבוהה לאישור');
      return;
    }

    if (!window.confirm(`לאשר ${highConfidence.length} הצעות ברמת ביטחון גבוהה (≥0.8)?`)) return;

    for (const item of highConfidence) {
      await handleApprove(item.id);
    }
  };

  // ─── Claude API ──────────────────────────────────────────────────

  const runAiMapping = async () => {
    if (!apiKey) {
      alert('נא להזין Claude API Key');
      return;
    }

    // Only map unmapped items
    const unmappedItems = masterItems.filter(i => !isMapped(i.id) && !suggestions[i.id]);
    if (unmappedItems.length === 0) {
      alert('כל הפריטים כבר ממופים או יש להם הצעות');
      return;
    }

    setAiRunning(true);
    setAiProgress({ current: 0, total: unmappedItems.length });

    // Build chapter list for the prompt
    const chapterList = bbChapters.map(c => `${c.code} - ${c.name_he}`).join('\n');

    for (let i = 0; i < unmappedItems.length; i++) {
      const item = unmappedItems[i];
      setAiProgress({ current: i + 1, total: unmappedItems.length });

      // Rate limit: wait 2s between requests to stay under API token limits
      if (i > 0) await new Promise(r => setTimeout(r, 2000));

      try {
        const itemTypeName = itemTypes[item.item_type_id] || 'לא ידוע';

        const response = await fetch('https://api.anthropic.com/v1/messages', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': apiKey,
            'anthropic-version': '2023-06-01',
            'anthropic-dangerous-direct-browser-access': 'true',
          },
          body: JSON.stringify({
            model: 'claude-sonnet-4-20250514',
            max_tokens: 200,
            messages: [
              {
                role: 'user',
                content: `You are mapping Israeli construction items to Blue Book (המפרט הכללי) chapters.

Item name: ${item.item_name}
Item type/category: ${itemTypeName}
${item.boq_description ? `BOQ description: ${item.boq_description}` : ''}

Available Blue Book chapters:
${chapterList}

Which chapter code best matches this item? If no chapter is a good match, use "98" (שונות).

Respond ONLY in this exact JSON format:
{"code": "XX", "confidence": 0.XX, "reasoning": "brief explanation in Hebrew"}`,
              },
            ],
          }),
        });

        if (!response.ok) {
          const errText = await response.text();
          console.error(`[AI MAP] API error for "${item.item_name}":`, errText);
          continue;
        }

        const data = await response.json();
        const text = data.content?.[0]?.text || '';

        // Parse the JSON response
        const jsonMatch = text.match(/\{[^}]+\}/);
        if (!jsonMatch) {
          console.error(`[AI MAP] Could not parse response for "${item.item_name}":`, text);
          continue;
        }

        const parsed = JSON.parse(jsonMatch[0]);
        const matchedChapter = bbChapters.find(c => c.code === parsed.code);

        if (!matchedChapter) {
          console.error(
            `[AI MAP] Unknown chapter code "${parsed.code}" for "${item.item_name}" — not in dropdown`,
          );
          continue;
        }

        console.log(`[AI MAP] ✓ "${item.item_name}" → ${parsed.code} (${parsed.confidence})`);

        // Save suggestion to DB
        const { error: insertError } = await supabase.from('bb_mapping_suggestions').upsert(
          {
            master_item_id: item.id,
            suggested_bb_chapter_id: matchedChapter.id,
            confidence: parsed.confidence,
            ai_reasoning: parsed.reasoning,
            status: 'pending',
          },
          { onConflict: 'master_item_id' },
        );

        if (insertError) {
          console.error(`Error saving suggestion for ${item.item_name}:`, insertError);
          continue;
        }

        // Update local state
        setSuggestions(prev => ({
          ...prev,
          [item.id]: {
            master_item_id: item.id,
            suggested_bb_chapter_id: matchedChapter.id,
            confidence: parsed.confidence,
            ai_reasoning: parsed.reasoning,
            status: 'pending',
          },
        }));
      } catch (err) {
        console.error(`Error processing ${item.item_name}:`, err);
      }
    }

    setAiRunning(false);
  };

  // ─── Bundle direct mapping ───────────────────────────────────────

  const handleBundleBBChange = async (bundleId, chapterId) => {
    try {
      const { error } = await supabase
        .from('master_items')
        .update({ bb_chapter_id: chapterId || null })
        .eq('id', bundleId);
      if (error) throw error;

      setBundleItems(prev =>
        prev.map(b => (b.id === bundleId ? { ...b, bb_chapter_id: chapterId || null } : b)),
      );
    } catch (err) {
      console.error('Error updating bundle BB chapter:', err);
      alert('שגיאה בשמירה: ' + err.message);
    }
  };

  const bundleStats = useMemo(() => {
    const total = bundleItems.length;
    const mapped = bundleItems.filter(b => b.bb_chapter_id).length;
    const pending = bundleItems.filter(
      b => !b.bb_chapter_id && suggestions[b.id]?.status === 'pending',
    ).length;
    return { total, mapped, unmapped: total - mapped, pending };
  }, [bundleItems, suggestions]);

  // AI mapping for bundles
  const runBundleAiMapping = async () => {
    if (!apiKey) {
      alert('נא להזין Claude API Key');
      return;
    }

    const unmapped = bundleItems.filter(b => !b.bb_chapter_id && !suggestions[b.id]);
    if (unmapped.length === 0) {
      alert('כל החבילות כבר ממופות או יש להן הצעות');
      return;
    }

    setAiRunning(true);
    setAiProgress({ current: 0, total: unmapped.length });

    const chapterList = bbChapters.map(c => `${c.code} - ${c.name_he}`).join('\n');

    for (let i = 0; i < unmapped.length; i++) {
      const bundle = unmapped[i];
      setAiProgress({ current: i + 1, total: unmapped.length });
      if (i > 0) await new Promise(r => setTimeout(r, 2000));

      try {
        const response = await fetch('https://api.anthropic.com/v1/messages', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': apiKey,
            'anthropic-version': '2023-06-01',
            'anthropic-dangerous-direct-browser-access': 'true',
          },
          body: JSON.stringify({
            model: 'claude-sonnet-4-20250514',
            max_tokens: 200,
            messages: [
              {
                role: 'user',
                content: `You are mapping Israeli construction item bundles to Blue Book (המפרט הכללי) chapters.

Bundle name: ${bundle.item_name}
This is a bundle (group of related items installed together).

Available Blue Book chapters:
${chapterList}

Which chapter code best matches this bundle? If no chapter is a good match, use "98" (שונות).

Respond ONLY in this exact JSON format:
{"code": "XX", "confidence": 0.XX, "reasoning": "brief explanation in Hebrew"}`,
              },
            ],
          }),
        });

        if (!response.ok) continue;

        const data = await response.json();
        const text = data.content?.[0]?.text || '';
        const jsonMatch = text.match(/\{[^}]+\}/);
        if (!jsonMatch) continue;

        const parsed = JSON.parse(jsonMatch[0]);
        const matchedChapter = bbChapters.find(c => c.code === parsed.code);
        if (!matchedChapter) continue;

        const { error: insertError } = await supabase.from('bb_mapping_suggestions').upsert(
          {
            master_item_id: bundle.id,
            suggested_bb_chapter_id: matchedChapter.id,
            confidence: parsed.confidence,
            ai_reasoning: parsed.reasoning,
            status: 'pending',
          },
          { onConflict: 'master_item_id' },
        );
        if (insertError) continue;

        setSuggestions(prev => ({
          ...prev,
          [bundle.id]: {
            master_item_id: bundle.id,
            suggested_bb_chapter_id: matchedChapter.id,
            confidence: parsed.confidence,
            ai_reasoning: parsed.reasoning,
            status: 'pending',
          },
        }));
      } catch (err) {
        console.error(`Error processing bundle ${bundle.item_name}:`, err);
      }
    }

    setAiRunning(false);
  };

  // Approve bundle AI suggestion → write directly to master_items.bb_chapter_id
  const handleBundleApprove = async bundleId => {
    const suggestion = suggestions[bundleId];
    if (!suggestion?.suggested_bb_chapter_id) return;

    await handleBundleBBChange(bundleId, suggestion.suggested_bb_chapter_id);

    // Mark suggestion as approved
    await supabase
      .from('bb_mapping_suggestions')
      .update({ status: 'approved' })
      .eq('master_item_id', bundleId);

    setSuggestions(prev => ({
      ...prev,
      [bundleId]: { ...prev[bundleId], status: 'approved' },
    }));
  };

  // Reject bundle AI suggestion
  const handleBundleReject = async bundleId => {
    await supabase
      .from('bb_mapping_suggestions')
      .update({ status: 'rejected' })
      .eq('master_item_id', bundleId);

    setSuggestions(prev => ({
      ...prev,
      [bundleId]: { ...prev[bundleId], status: 'rejected' },
    }));
  };

  const filteredBundles = useMemo(() => {
    if (!searchTerm) return bundleItems;
    const term = searchTerm.toLowerCase();
    return bundleItems.filter(b => b.item_name.toLowerCase().includes(term));
  }, [bundleItems, searchTerm]);

  // ─── Render helpers ──────────────────────────────────────────────

  const getChapterName = chapterId => {
    const ch = bbChapters.find(c => c.id === chapterId);
    return ch ? `${ch.code} - ${ch.name_he}` : '';
  };

  // Build hierarchical chapter options: chapters first, then indented sections
  const chapterOptions = useMemo(() => {
    const depth0 = bbChapters.filter(c => c.depth === 0);
    const result = [];
    depth0.forEach(ch => {
      result.push(ch);
      const sections = bbChapters
        .filter(s => s.depth === 1 && s.parent_id === ch.id)
        .sort((a, b) => a.code.localeCompare(b.code));
      sections.forEach(s => result.push(s));
    });
    return result;
  }, [bbChapters]);

  // Map all items of a type to a specific chapter
  const handleMapByType = async (typeId, chapterId) => {
    const items = masterItems.filter(i => i.item_type_id === typeId && !isMapped(i.id));
    if (items.length === 0) {
      alert('אין פריטים לא ממופים לסוג זה');
      return;
    }
    const typeName = itemTypes[typeId] || typeId;
    const chName = getChapterName(chapterId);
    if (!window.confirm(`למפות ${items.length} פריטים מסוג "${typeName}" ל${chName}?`)) return;
    for (const item of items) {
      await handleManualMap(item.id, chapterId);
    }
    setMapByTypeId('');
  };

  const getConfidenceClass = confidence => {
    if (confidence >= 0.8) return 'confidence-high';
    if (confidence >= 0.5) return 'confidence-medium';
    return 'confidence-low';
  };

  // ─── Render ──────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="bb-mapping-page">
        <div className="bb-mapping-loading">טוען נתונים...</div>
      </div>
    );
  }

  return (
    <div className="bb-mapping-page">
      {/* Header */}
      <div className="bb-mapping-header">
        <h1>מיפוי פריטים לפרקי ספר כחול</h1>
        <div className="bb-mapping-header-actions">
          <input
            type="text"
            className="bb-mapping-search"
            placeholder="חיפוש פריט..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="bb-mapping-filters" style={{ marginBottom: 16 }}>
        <button
          className={`bb-mapping-filter-btn ${activeTab === 'items' ? 'active' : ''}`}
          onClick={() => setActiveTab('items')}
        >
          פריטים ({stats.total})
        </button>
        <button
          className={`bb-mapping-filter-btn ${activeTab === 'bundles' ? 'active' : ''}`}
          onClick={() => setActiveTab('bundles')}
        >
          חבילות ({bundleStats.total})
        </button>
      </div>

      {activeTab === 'bundles' ? (
        <>
          {/* Bundle progress */}
          <div className="bb-mapping-progress">
            <div className="bb-mapping-progress-bar">
              <div
                className="bb-mapping-progress-fill"
                style={{
                  width: `${bundleStats.total ? (bundleStats.mapped / bundleStats.total) * 100 : 0}%`,
                }}
              />
            </div>
            <div className="bb-mapping-progress-text">
              {bundleStats.mapped}/{bundleStats.total} ממופים
            </div>
          </div>

          {/* AI actions for bundles */}
          <div className="api-key-section">
            <span className="api-key-label">
              Claude API: {apiKey ? '✓ מחובר' : '✗ חסר REACT_APP_CLAUDE_API_KEY ב-.env'}
            </span>
            <button
              className="bb-action-btn primary"
              onClick={runBundleAiMapping}
              disabled={aiRunning || !apiKey}
            >
              {aiRunning
                ? `ממפה... (${aiProgress.current}/${aiProgress.total})`
                : `הרץ מיפוי AI (${bundleStats.unmapped - bundleStats.pending} חסרים)`}
            </button>
          </div>

          {/* Bundle table */}
          <div className="bb-mapping-table-container">
            <table className="bb-mapping-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>שם חבילה</th>
                  <th>הצעת AI</th>
                  <th>מיפוי לפרק</th>
                </tr>
              </thead>
              <tbody>
                {filteredBundles.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="bb-mapping-empty">
                      אין חבילות להצגה
                    </td>
                  </tr>
                ) : (
                  filteredBundles.map((bundle, idx) => {
                    const suggestion = suggestions[bundle.id];
                    return (
                      <tr key={bundle.id}>
                        <td>{idx + 1}</td>
                        <td>
                          <strong>{bundle.item_name}</strong>
                        </td>
                        <td>
                          <div className="bb-mapping-actions-cell">
                            {suggestion?.suggested_bb_chapter_id
                              ? getChapterName(suggestion.suggested_bb_chapter_id)
                              : '-'}
                            {suggestion?.status === 'pending' &&
                              suggestion?.suggested_bb_chapter_id && (
                                <>
                                  <button
                                    className="bb-action-btn approve"
                                    onClick={() => handleBundleApprove(bundle.id)}
                                  >
                                    אשר
                                  </button>
                                  <button
                                    className="bb-action-btn reject"
                                    onClick={() => handleBundleReject(bundle.id)}
                                  >
                                    דחה
                                  </button>
                                </>
                              )}
                            {suggestion?.status === 'approved' && (
                              <span className="status-badge status-approved">אושר</span>
                            )}
                            {suggestion?.status === 'rejected' && (
                              <span className="status-badge status-rejected">נדחה</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <select
                            className={`chapter-select${bundle.bb_chapter_id ? ' mapped' : ''}`}
                            value={bundle.bb_chapter_id || ''}
                            onChange={e => handleBundleBBChange(bundle.id, e.target.value)}
                          >
                            <option value="">-- בחר פרק --</option>
                            {chapterOptions.map(ch => (
                              <option key={ch.id} value={ch.id}>
                                {ch.depth === 1
                                  ? `  ${ch.code} - ${ch.name_he}`
                                  : `${ch.code} - ${ch.name_he}`}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <>
          {/* Progress */}
          <div className="bb-mapping-progress">
            <div className="bb-mapping-progress-bar">
              <div
                className="bb-mapping-progress-fill"
                style={{ width: `${stats.total ? (stats.mapped / stats.total) * 100 : 0}%` }}
              />
            </div>
            <div className="bb-mapping-progress-text">
              {stats.mapped}/{stats.total} ממופים
              {stats.pending > 0 && ` | ${stats.pending} ממתינים לאישור`}
            </div>
          </div>

          {/* AI actions */}
          <div className="api-key-section">
            <span className="api-key-label">
              Claude API: {apiKey ? '✓ מחובר' : '✗ חסר REACT_APP_CLAUDE_API_KEY ב-.env'}
            </span>
            <button
              className="bb-action-btn primary"
              onClick={runAiMapping}
              disabled={aiRunning || !apiKey}
            >
              {aiRunning
                ? `ממפה... (${aiProgress.current}/${aiProgress.total})`
                : `הרץ מיפוי AI (${stats.unmapped - Object.values(suggestions).filter(s => s.status === 'pending').length} חסרים)`}
            </button>
            <button
              className="bb-action-btn approve"
              onClick={handleBatchApprove}
              disabled={aiRunning}
            >
              אשר הכל (ביטחון ≥0.8)
            </button>
            <select
              className="chapter-select"
              style={{ minWidth: 160 }}
              value=""
              onChange={e => {
                if (e.target.value) handleRejectByType(e.target.value);
              }}
            >
              <option value="">דחה הכל לפי סוג...</option>
              {Object.entries(itemTypes)
                .filter(([id]) =>
                  masterItems.some(
                    i => i.item_type_id === id && suggestions[i.id]?.status === 'pending',
                  ),
                )
                .sort((a, b) => a[1].localeCompare(b[1], 'he'))
                .map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
            </select>
          </div>

          {/* Bulk map by type */}
          <div className="api-key-section">
            <span className="api-key-label">מפה הכל לפי סוג:</span>
            <select
              className="chapter-select"
              style={{ minWidth: 160 }}
              value={mapByTypeId}
              onChange={e => setMapByTypeId(e.target.value)}
            >
              <option value="">בחר סוג...</option>
              {Object.entries(itemTypes)
                .filter(([id]) => masterItems.some(i => i.item_type_id === id && !isMapped(i.id)))
                .sort((a, b) => a[1].localeCompare(b[1], 'he'))
                .map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
            </select>
            <select
              className="chapter-select"
              style={{ minWidth: 220 }}
              value=""
              disabled={!mapByTypeId}
              onChange={e => {
                if (e.target.value && mapByTypeId) handleMapByType(mapByTypeId, e.target.value);
              }}
            >
              <option value="">בחר פרק...</option>
              {chapterOptions.map(ch => (
                <option key={ch.id} value={ch.id}>
                  {ch.depth === 1 ? `  ${ch.code} - ${ch.name_he}` : `${ch.code} - ${ch.name_he}`}
                </option>
              ))}
            </select>
          </div>

          {/* Filters */}
          <div className="bb-mapping-filters">
            {[
              { key: 'all', label: `הכל (${stats.total})` },
              { key: 'unmapped', label: `לא ממופים (${stats.unmapped})` },
              { key: 'pending', label: `ממתינים (${stats.pending})` },
              { key: 'approved', label: `ממופים (${stats.mapped})` },
              { key: 'low-confidence', label: 'ביטחון נמוך' },
            ].map(f => (
              <button
                key={f.key}
                className={`bb-mapping-filter-btn ${filter === f.key ? 'active' : ''}`}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Table */}
          <div className="bb-mapping-table-container">
            <table className="bb-mapping-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>שם פריט</th>
                  <th>סוג</th>
                  <th>הצעת AI</th>
                  <th>ביטחון</th>
                  <th>מיפוי נוכחי</th>
                  <th>נימוק</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="bb-mapping-empty">
                      אין פריטים להצגה
                    </td>
                  </tr>
                ) : (
                  filteredItems.map((item, idx) => {
                    const suggestion = suggestions[item.id];

                    return (
                      <tr key={item.id}>
                        <td>{idx + 1}</td>
                        <td>
                          <strong>{item.item_name}</strong>
                        </td>
                        <td>
                          <span className="item-type-badge">
                            {itemTypes[item.item_type_id] || '-'}
                          </span>
                        </td>
                        <td>
                          <div className="bb-mapping-actions-cell">
                            {suggestion?.suggested_bb_chapter_id
                              ? getChapterName(suggestion.suggested_bb_chapter_id)
                              : '-'}
                            {suggestion?.status === 'pending' &&
                              suggestion?.suggested_bb_chapter_id && (
                                <>
                                  <button
                                    className="bb-action-btn approve"
                                    onClick={() => handleApprove(item.id)}
                                  >
                                    אשר
                                  </button>
                                  <button
                                    className="bb-action-btn reject"
                                    onClick={() => handleReject(item.id)}
                                  >
                                    דחה
                                  </button>
                                </>
                              )}
                            {suggestion?.status === 'rejected' && (
                              <span className="status-badge status-rejected">נדחה</span>
                            )}
                            {suggestion?.status === 'approved' && (
                              <span className="status-badge status-approved">אושר</span>
                            )}
                          </div>
                        </td>
                        <td>
                          {suggestion?.confidence != null ? (
                            <span
                              className={`confidence-badge ${getConfidenceClass(suggestion.confidence)}`}
                            >
                              {(suggestion.confidence * 100).toFixed(0)}%
                            </span>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <select
                              className={`chapter-select${getMappedChapterId(item.id) ? ' mapped' : ''}`}
                              value={getMappedChapterId(item.id)}
                              onChange={e => handleManualMap(item.id, e.target.value)}
                            >
                              <option value="">-- בחר פרק --</option>
                              {chapterOptions.map(ch => (
                                <option key={ch.id} value={ch.id}>
                                  {ch.depth === 1
                                    ? `  ${ch.code} - ${ch.name_he}`
                                    : `${ch.code} - ${ch.name_he}`}
                                </option>
                              ))}
                            </select>
                            {getMappedChapterId(item.id) && (
                              <button
                                className="bb-action-btn reject"
                                style={{ flexShrink: 0 }}
                                onClick={() => handleClearMapping(item.id)}
                                title="נקה מיפוי"
                              >
                                ✕
                              </button>
                            )}
                          </div>
                        </td>
                        <td>
                          {suggestion?.ai_reasoning ? (
                            <span className="ai-reasoning" title={suggestion.ai_reasoning}>
                              {suggestion.ai_reasoning}
                            </span>
                          ) : (
                            '-'
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default BBMappingPage;
